import moment from "moment";

import { calculateAverage } from "./CalculateAverage";
import { UnitConversions } from "./UnitConversions";
import { Club, MeasuringSystem, Shot, ShotExtra, Target } from "../SessionModel";

import { getClubOrderIndex, getClubColor, getClubTypeAbbreviation, getClubId, getClubName } from "../../../config/ClubInfo";

/**
 * Earliest shot of a club, used to break ties between clubs sharing a club type
 * (e.g. three 4i). The payload carries no club creation time, so first use is the
 * closest available proxy for "the club added first is displayed first".
 */
const getFirstShotTimestamp = (club: Club) => {
    return club.shots.reduce((earliest, shot) => Math.min(earliest, shot.timestamp), Infinity);
}

export const generateClubGroup = (shots: Shot[], isNormalized: boolean, measuringSystem: MeasuringSystem, existingClubs?: Club[], location?: string) => {
    let clubs: Club[] = shots.reduce((clubGroup: Club[], shot: Shot, index: number) => {
        let clubIndex = clubGroup.findIndex((club: Club) => club.clubId === getClubId(shot));

        let shotExtra: ShotExtra = {
            shotIndex: index,
            timestampConverted: moment(shot.timestamp * 1000).format('M/D/YYYY HH:mm:ss'),

            faceToPath: shot.faceAngle - shot.clubPath,
            faceToPathConverted: UnitConversions("faceToPath", shot, isNormalized, measuringSystem, location),
            carryDistanceConverted: UnitConversions("carryDistance", shot, isNormalized, measuringSystem, location),
            distanceToPinConverted: UnitConversions("distanceToPin", shot, isNormalized, measuringSystem, location),
            spinRateConverted: UnitConversions("spinRate", shot, isNormalized, measuringSystem, location),
            ballSpeedConverted: UnitConversions("ballSpeed", shot, isNormalized, measuringSystem, location),
            clubSpeedConverted: UnitConversions("clubSpeed", shot, isNormalized, measuringSystem, location),
            smashFactorConverted: UnitConversions("smashFactor", shot, isNormalized, measuringSystem, location),
            apexConverted: UnitConversions("apex", shot, isNormalized, measuringSystem, location),
            launchAngleConverted: UnitConversions("launchAngle", shot, isNormalized, measuringSystem, location),
            descentAngleConverted: UnitConversions("descentAngle", shot, isNormalized, measuringSystem, location),

            clubPathConverted: UnitConversions("clubPath", shot, isNormalized, measuringSystem, location),
            faceAngleConverted: UnitConversions("faceAngle", shot, isNormalized, measuringSystem, location),
            attackAngleConverted: UnitConversions("attackAngle", shot, isNormalized, measuringSystem, location),
            spinAxisConverted: UnitConversions("spinAxis", shot, isNormalized, measuringSystem, location),
            horizontalLaunchAngleConverted: UnitConversions("horizontalLaunchAngle", shot, isNormalized, measuringSystem, location),
            sideConverted: UnitConversions("side", shot, isNormalized, measuringSystem, location),

            sideTotalConverted: UnitConversions("sideTotal", shot, isNormalized, measuringSystem, location),
            totalDistanceConverted: UnitConversions("totalDistance", shot, isNormalized, measuringSystem, location),

            normalizedApexConverted: UnitConversions("apex", shot, true, measuringSystem, location),
            normalizedSideConverted: UnitConversions("side", shot, true, measuringSystem, location),
            normalizedSideTotalConverted: UnitConversions("sideTotal", shot, true, measuringSystem, location),
            normalizedCarryDistanceConverted: UnitConversions("carryDistance", shot, true, measuringSystem, location),
            normalizedTotalDistanceConverted: UnitConversions("totalDistance", shot, true, measuringSystem, location),

            isActive: true,
            isSelected: false,
            isNormalized: isNormalized,
            selectedTimestamp: 0,
            ...shot
        };

        if (clubIndex > -1) {
            clubGroup[clubIndex].shots.push(shotExtra);
            clubGroup[clubIndex].totalShots += 1;
            clubGroup[clubIndex].average = calculateAverage(clubGroup[clubIndex].shots, measuringSystem);
        } else {
            let isShowShots = false;
            if (existingClubs && existingClubs.length) {
                let selectedClub = existingClubs.find(club => club.clubId === getClubId(shot));
                if (selectedClub) isShowShots = selectedClub.isShowShots;
            }

            let clubData: Club = {
                clubId: getClubId(shot),
                isActive: true,
                isShowShots,
                isNormalized: isNormalized,
                clubName: getClubName(shot.clubType),
                clubType: shot.clubType,
                clubCategory: shot.clubCategory,
                clubNickname: shot.clubName,
                clubColor: getClubColor(shot.clubType, shot.clubColor),
                clubIcon: getClubTypeAbbreviation(shot.clubType),
                totalShots: 1,
                average: calculateAverage([shotExtra], measuringSystem),
                shots: [shotExtra],
                selectedTimestamp: 0
            };
            clubGroup.push(clubData);
        }
        return clubGroup;
    }, [])

    clubs = clubs.sort((a, b) => {
        const orderDiff = getClubOrderIndex(a.clubType) - getClubOrderIndex(b.clubType);
        if (orderDiff !== 0) return orderDiff;
        return getFirstShotTimestamp(a) - getFirstShotTimestamp(b);
    });

    // Newest shot first within each club, older ones below. Some sessions report the
    // same timestamp for every shot, which leaves the comparator with nothing to order
    // by; fall back to the shot's position in the raw payload (assigned above, before
    // any sorting) so those sessions still read newest-first instead of keeping the
    // payload's oldest-first order.
    clubs.forEach(club => {
        club.shots.sort((a, b) => b.timestamp - a.timestamp || b.shotIndex - a.shotIndex);
    });

    return clubs;
}
export const generateTargetGroup = (shots: Shot[], isNormalized: boolean, measuringSystem: MeasuringSystem, existingTargets?: Target[], drillTargets?: any, location?: string) => {
    let targets: Target[] = shots.reduce((targetGroup: Target[], shot: Shot, index: number) => {
        let targetIndex = targetGroup.findIndex((target: Target) => target.targetId === shot.lmDrillTargetId);

        let shotExtra: ShotExtra = {
            shotIndex: index,
            timestampConverted: moment(shot.timestamp * 1000).format('M/D/YYYY HH:mm:ss'),

            faceToPath: shot.faceAngle - shot.clubPath,
            faceToPathConverted: UnitConversions("faceToPath", shot, isNormalized, measuringSystem, location),
            carryDistanceConverted: UnitConversions("carryDistance", shot, isNormalized, measuringSystem, location),
            distanceToPinConverted: UnitConversions("distanceToPin", shot, isNormalized, measuringSystem, location),
            spinRateConverted: UnitConversions("spinRate", shot, isNormalized, measuringSystem, location),
            ballSpeedConverted: UnitConversions("ballSpeed", shot, isNormalized, measuringSystem, location),
            clubSpeedConverted: UnitConversions("clubSpeed", shot, isNormalized, measuringSystem, location),
            smashFactorConverted: UnitConversions("smashFactor", shot, isNormalized, measuringSystem, location),
            apexConverted: UnitConversions("apex", shot, isNormalized, measuringSystem, location),
            launchAngleConverted: UnitConversions("launchAngle", shot, isNormalized, measuringSystem, location),
            descentAngleConverted: UnitConversions("descentAngle", shot, isNormalized, measuringSystem, location),

            clubPathConverted: UnitConversions("clubPath", shot, isNormalized, measuringSystem, location),
            faceAngleConverted: UnitConversions("faceAngle", shot, isNormalized, measuringSystem, location),
            attackAngleConverted: UnitConversions("attackAngle", shot, isNormalized, measuringSystem, location),
            spinAxisConverted: UnitConversions("spinAxis", shot, isNormalized, measuringSystem, location),
            horizontalLaunchAngleConverted: UnitConversions("horizontalLaunchAngle", shot, isNormalized, measuringSystem, location),
            sideConverted: UnitConversions("side", shot, isNormalized, measuringSystem, location),
            sideTotalConverted: UnitConversions("sideTotal", shot, isNormalized, measuringSystem, location),
            totalDistanceConverted: UnitConversions("totalDistance", shot, isNormalized, measuringSystem, location),

            normalizedApexConverted: UnitConversions("apex", shot, true, measuringSystem, location),
            normalizedSideConverted: UnitConversions("side", shot, true, measuringSystem, location),
            normalizedSideTotalConverted: UnitConversions("sideTotal", shot, true, measuringSystem, location),
            normalizedCarryDistanceConverted: UnitConversions("carryDistance", shot, true, measuringSystem, location),
            normalizedDistanceToPinConverted: UnitConversions("distanceToPin", shot, true, measuringSystem, location),
            normalizedTotalDistanceConverted: UnitConversions("totalDistance", shot, true, measuringSystem, location),

            isActive: true,
            isSelected: false,
            isNormalized: isNormalized,
            selectedTimestamp: 0,

            clubIcon: getClubTypeAbbreviation(shot.clubType),
            ...shot,
            clubColor: getClubColor(shot.clubType, shot.clubColor)
        };

        if (targetIndex > -1) {
            targetGroup[targetIndex].shots.push(shotExtra);
            targetGroup[targetIndex].shots = targetGroup[targetIndex].shots.sort((a, b) => {
                return getClubOrderIndex(a.clubType) - getClubOrderIndex(b.clubType);
            });
            targetGroup[targetIndex].totalShots += 1;
            targetGroup[targetIndex].average = calculateAverage(targetGroup[targetIndex].shots, measuringSystem);
        } else {
            let isShowShots = false;
            if (existingTargets && existingTargets.length) {
                let selectedTarget = existingTargets.find(target => target.targetId === shot.lmDrillTargetId);
                if (selectedTarget) isShowShots = selectedTarget.isShowShots;
            }

            let targetData: Target = {
                targetId: shot.lmDrillTargetId,
                isActive: true,
                isShowShots,
                isNormalized: isNormalized,
                totalShots: 1,
                average: calculateAverage([shotExtra], measuringSystem),
                shots: [shotExtra],
                drillTarget: drillTargets?.find((target: any) => target.id === shot.lmDrillTargetId)
            };
            targetData.shots = targetData.shots.sort((a, b) => {
                return getClubOrderIndex(a.clubType) - getClubOrderIndex(b.clubType);
            });
            targetGroup.push(targetData);
        }
        return targetGroup;
    }, [])

    targets = targets.sort((a, b) => {
        return a.drillTarget?.distance?.distance - b.drillTarget?.distance?.distance;
    });
    targets.push(targets.shift() as Target);

    return targets;
}

export const getFormattedClubName = (clubName: string) => {
    let formattedClubName = clubName.replaceAll("Iron", "iron").replaceAll("Hybrid", "hybrid").replaceAll("Wood", "wood");
    return formattedClubName
}
